import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  IconBuildingStore,
  IconChevronDown,
  IconCirclePlus,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { Coordinates, Product, StoreSummary } from 'shared-types'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { useSession, useStoresList, useUserPreferredStore } from '~/hooks'
import { HoverTooltip, LoadingSpinner } from '../../../components'
import styles from './ProductDashboardShippingMethods.module.scss'

export type ProductDashboardStoreAvailabilityStatus = {
  store: Pick<StoreSummary, 'name' | 'storeKey'>
  clickAndCollect: 'available' | 'notAvailable'
  inStore: 'available' | 'notAvailable'
  sku?: string
  specialOrder?: boolean
  isEndlessAisle?: boolean
  isEndlessAisleFromSupplier?: boolean
  isEndlessAisleOnHand?: boolean
  inStoreEndlessAisle?: boolean
}

type StoreAvailabilitySectionProps = {
  product: Product
  storeAvailabilityStatus?: ProductDashboardStoreAvailabilityStatus
}

const STORES_LIMIT = 5

export const StoreAvailabilitySection = ({
  product,
  storeAvailabilityStatus,
}: StoreAvailabilitySectionProps) => {
  const { sku } = product
  const {
    store,
    specialOrder,
    clickAndCollect,
    isEndlessAisle,
    isEndlessAisleFromSupplier,
    isEndlessAisleOnHand,
    inStoreEndlessAisle,
  } = storeAvailabilityStatus || {}

  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false)

  const {
    store: selectedStore,
    canClickAndCollect,
    promptUserToSelectStore,
    selectStore,
    isFetchingStore,
  } = useUserPreferredStore()
  const { session } = useSession()

  const storeKey = useRef(selectedStore?.storeKey)

  const { stores, isLoading, fetchStoresByLocation } = useStoresList()

  const storesToDisplay = useMemo(() => {
    if (stores == null || stores.length === 0) {
      return []
    }

    if (selectedStore == null) {
      return stores.slice(0, STORES_LIMIT - 1)
    }

    return stores.filter((store) => {
      return store.storeKey !== selectedStore.storeKey
    })
  }, [selectedStore, stores])

  const isEndlessAisleOnHandWithoutStock =
    isEndlessAisleOnHand && !inStoreEndlessAisle

  const loadNearbyStores = useCallback(async () => {
    console.log(selectedStore)

    const latitude = selectedStore?.latitude ?? null
    const longitude = selectedStore?.longitude ?? null

    if (latitude == null || longitude == null) {
      return
    }

    const location: Coordinates = {
      latitude: Number(latitude),
      longitude: Number(longitude),
    }

    await fetchStoresByLocation(
      location,
      session,
      STORES_LIMIT,
      sku,
      sku && canClickAndCollect,
      selectedStore.storeKey
    )
  }, [canClickAndCollect, fetchStoresByLocation, selectedStore, session, sku])

  const openStoreDropdown = () => {
    setIsStoreDropdownOpen(true)
  }

  const closeStoreDropdown = () => {
    setIsStoreDropdownOpen(false)
  }

  const toggleStoreDropdown = () => {
    if (isStoreDropdownOpen) {
      closeStoreDropdown()
    } else {
      openStoreDropdown()
    }
  }

  // The preferred store resolves asynchronously, so the dropdown can be opened
  // before its coordinates are known. Fetch as soon as they arrive.
  useEffect(() => {
    if (!isStoreDropdownOpen) {
      return
    }

    loadNearbyStores()
  }, [isStoreDropdownOpen, loadNearbyStores])

  // Only close when the user genuinely switches store - `selectedStore` dips to
  // undefined whenever the preferred store query refetches.
  useEffect(() => {
    const nextStoreKey = selectedStore?.storeKey

    if (nextStoreKey == null || nextStoreKey === storeKey.current) {
      return
    }

    const hadPreviousStore = storeKey.current != null
    storeKey.current = nextStoreKey

    if (hadPreviousStore) {
      closeStoreDropdown()
    }
  }, [selectedStore?.storeKey])

  const renderStoreIcon = () => {
    return <IconBuildingStore size={28} color='var(--color-pdp-primary)' />
  }

  const renderStoreStockStatusIcon = () => {
    let statusCssClass = 'available'

    if (specialOrder && !isEndlessAisle) {
      statusCssClass = ''
    } else if (isEndlessAisle && !inStoreEndlessAisle) {
      statusCssClass = 'endlessAisle'
    } else if (clickAndCollect === 'notAvailable') {
      statusCssClass = 'outOfStock'
    }

    return (
      <div className={classNames(styles.statusDot, styles[statusCssClass])} />
    )
  }

  const renderEndlessAisleStatusLabel = () => {
    if (!isEndlessAisle || inStoreEndlessAisle) {
      return null
    }

    return 'Order for Click & Collect'
  }

  const renderSpecialOrderStatusLabel = () => {
    if (!specialOrder || isEndlessAisle) {
      return null
    }

    return 'Special Order'
  }

  const renderRegularStatusLabel = () => {
    if (
      specialOrder ||
      isEndlessAisleFromSupplier ||
      isEndlessAisleOnHandWithoutStock
    ) {
      return null
    }

    if (clickAndCollect !== 'available') {
      return (
        <>
          Out of stock at{' '}
          <button
            type='button'
            className={styles.noActiveOutline}
            onClick={toggleStoreDropdown}>
            <Typography
              className={styles.storeNameButtonLabel}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.span}>
              {store.name}
            </Typography>
          </button>
        </>
      )
    }

    return (
      <>
        Ready in 1 hour at{' '}
        <button
          type='button'
          className={styles.noActiveOutline}
          onClick={toggleStoreDropdown}>
          <Typography
            className={styles.storeNameButtonLabel}
            variant={TypographyVariant.BodySmallBold}
            tag={TypographyTag.span}>
            {store.name}
          </Typography>
        </button>
      </>
    )
  }

  const renderStoreDropdownStockStatus = (store: StoreSummary) => {
    const { isClickAndCollectAvailable, isEndlessAisle } = store

    let label = 'In Stock'

    if (isEndlessAisle) {
      label = 'Direct From Supplier'
    } else if (!isClickAndCollectAvailable) {
      label = 'Out of Stock'
    }

    return (
      <div className={styles.storeItemStockStatus}>
        <div
          className={classNames(styles.storeItemStockStatusIcon, {
            [styles.endlessAisle]: isEndlessAisle,
            [styles.outOfStock]: !isClickAndCollectAvailable && !isEndlessAisle,
          })}
        />
        <Typography
          className={styles.storeItemStockStatusLabel}
          variant={TypographyVariant.BodySmallBold}
          tag={TypographyTag.div}>
          {label}
        </Typography>
      </div>
    )
  }

  const generateStoreAddress = (store: StoreSummary) => {
    const { streetNumber, streetName, town, state, postalCode, country } =
      store.address || {}

    let address = ''

    if (streetNumber) {
      address += `${streetNumber} `
    }

    if (streetName) {
      address += `${streetName}, `
    }

    if (town) {
      address += `${town}, `
    }

    address += `${state} ${postalCode} ${country}`

    return address
  }

  const renderDropdownItemTooltipContent = (store: StoreSummary) => {
    return (
      <div>
        <Typography
          className={styles.dropdownTooltipStoreName}
          variant={TypographyVariant.BodySmallBold}
          tag={TypographyTag.div}>
          {store.displayName}
        </Typography>
        <Typography
          className={styles.dropdownTooltipAddress}
          variant={TypographyVariant.BodySmall}
          tag={TypographyTag.div}>
          {generateStoreAddress(store)}
        </Typography>
        <Typography
          className={styles.dropdownTooltipDistance}
          variant={TypographyVariant.BodySmall}
          tag={TypographyTag.div}>
          {store.distance}km away
        </Typography>
      </div>
    )
  }

  const renderStoreDropdown = () => {
    if (!isStoreDropdownOpen) {
      return null
    }

    if (isLoading || isFetchingStore) {
      return (
        <div className={styles.storeDropdownLoading}>
          <LoadingSpinner size='sm' />
        </div>
      )
    }

    return (
      <div className={styles.storeDropdown}>
        {storesToDisplay?.map((store) => {
          return (
            <div key={store.storeKey} className={styles.storeDropdownItem}>
              <div>
                <HoverTooltip
                  content={renderDropdownItemTooltipContent(store)}
                  placement='bottom-left'>
                  <Typography
                    className={styles.storeItemName}
                    variant={TypographyVariant.BodySmallBold}
                    tag={TypographyTag.div}>
                    {store.displayName}
                  </Typography>
                </HoverTooltip>
              </div>
              <div>
                <button
                  type='button'
                  className={classNames(
                    styles.storeItemButton,
                    styles.noActiveOutline
                  )}
                  onClick={() => {
                    if (selectedStore?.storeKey === store.storeKey) {
                      return
                    }

                    selectStore(
                      store.storeKey,
                      store.supplyChannelId,
                      store.displayName
                    )
                  }}>
                  <Typography
                    className={styles.storeItemButtonLabel}
                    variant={TypographyVariant.BodySmallBold}
                    tag={TypographyTag.span}>
                    Select Store
                  </Typography>
                </button>
              </div>
              {renderStoreDropdownStockStatus(store)}
            </div>
          )
        })}
        <div key={store.storeKey} className={styles.storeDropdownItem}>
          <button
            type='button'
            className={classNames(
              styles.moreNearbyStoresButton,
              styles.noActiveOutline
            )}
            onClick={() => {
              promptUserToSelectStore(sku)
            }}>
            <IconCirclePlus size={16} color='var(--color-pdp-primary)' />
            <Typography
              className={styles.moreNearbyStoresLabel}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.span}>
              Click for more nearby stores
            </Typography>
          </button>
        </div>
      </div>
    )
  }

  const renderStoreSection = () => {
    return (
      <>
        <div className={styles.shippingMethod}>
          <div>{renderStoreIcon()}</div>
          {renderStoreStockStatusIcon()}
          <div className={styles.shippingMethodDetails}>
            <div className={styles.shippingMethodInfo}>
              <Typography
                className={styles.shippingMethodName}
                variant={TypographyVariant.BodySmallBold}
                tag={TypographyTag.div}>
                Click & Collect
              </Typography>
              <Typography
                className={styles.shippingMethodDescription}
                variant={TypographyVariant.BodySmall}
                tag={TypographyTag.div}>
                {renderRegularStatusLabel()}
                {renderSpecialOrderStatusLabel()}
                {renderEndlessAisleStatusLabel()}
              </Typography>
            </div>
            <button
              type='button'
              className={styles.noActiveOutline}
              onClick={toggleStoreDropdown}>
              <Typography
                className={styles.shippingMethodAction}
                variant={TypographyVariant.BodySmallBold}
                tag={TypographyTag.div}>
                <span>Show Nearby Stores</span>
                <IconChevronDown
                  size={14}
                  className={classNames(styles.storeDropdownArrow, {
                    [styles.inverted]: isStoreDropdownOpen,
                  })}
                />
              </Typography>
            </button>
          </div>
          {clickAndCollect === 'available' && (
            <Typography
              className={classNames(
                styles.shippingMethodPrice,
                styles.freePrice
              )}
              variant={TypographyVariant.BodyRegularBold}
              tag={TypographyTag.div}>
              Free
            </Typography>
          )}
        </div>
        {renderStoreDropdown()}
      </>
    )
  }

  const renderNoStoreSection = () => {
    return (
      <button
        type='button'
        className={classNames(styles.shippingMethod, styles.noActiveOutline)}
        onClick={() => {
          promptUserToSelectStore(sku)
        }}>
        <div>{renderStoreIcon()}</div>
        <div className={styles.statusDot} />
        <div className={styles.shippingMethodDetails}>
          <div className={styles.shippingMethodInfo}>
            <Typography
              className={styles.shippingMethodName}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.div}>
              Click & Collect
            </Typography>
          </div>
          <div>
            <Typography
              className={styles.shippingMethodAction}
              variant={TypographyVariant.BodySmallBold}
              tag={TypographyTag.div}>
              Select a store
            </Typography>
          </div>
        </div>
      </button>
    )
  }

  if (store == null) {
    return renderNoStoreSection()
  }

  return renderStoreSection()
}
